import React, { useRef, useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import {
  MessageCircle,
  FileText,
  Smartphone,
  Package,
  MapPin,
  Snowflake,
  QrCode,
  ArrowUpRight,
  Instagram,
} from "lucide-react";
import { orderSteps, siteInfo, navLinks } from "../../data/mock";

const stepIconMap = {
  MessageCircle: MessageCircle,
  FileText: FileText,
  Smartphone: Smartphone,
  Package: Package,
};

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [threshold]);
  return [ref, isVisible];
};

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/* ===== Payment & Ordering Section ===== */
export const PaymentSection = () => {
  const [ref, isVisible] = useInView(0.05);

  return (
    <section id="ordering" ref={ref} className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Heading */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-sm font-semibold text-sky-500 tracking-wider uppercase">
            Simple Process
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
            Easy Ordering & UPI Payments
          </h2>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
            Getting your 3D printed product is as easy as 1-2-3-4. Simple
            process, seamless experience.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Steps */}
          <div className="space-y-1">
            {orderSteps.map((step, index) => {
              const Icon = stepIconMap[step.icon];
              return (
                <div
                  key={step.step}
                  className={`flex gap-5 items-start py-5 transition-all duration-600 ${
                    isVisible
                      ? "opacity-100 translate-x-0"
                      : "opacity-0 -translate-x-8"
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="flex-shrink-0 relative">
                    <div className="w-14 h-14 rounded-2xl bg-sky-50 border border-sky-100/60 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-sky-600" />
                    </div>
                    {index < orderSteps.length - 1 && (
                      <div className="absolute top-14 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-sky-100" />
                    )}
                  </div>
                  <div className="pt-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-sky-500 tracking-wide">
                        STEP {step.step}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {step.title}
                    </h3>
                    <p className="text-slate-500 text-sm mt-1 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* UPI QR Code Card */}
          <div
            className={`transition-all duration-700 delay-300 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <Card className="border border-sky-100/60 bg-gradient-to-b from-white to-sky-50/30 overflow-hidden shadow-lg shadow-sky-100/20">
              <CardContent className="p-8 sm:p-10 text-center">
                <div className="w-16 h-16 rounded-2xl bg-sky-50 border border-sky-100/60 flex items-center justify-center mx-auto mb-6">
                  <QrCode className="w-8 h-8 text-sky-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Scan to Pay via UPI
                </h3>
                <p className="text-slate-500 text-sm mb-8">
                  Quick and secure payment directly from your phone
                </p>

                {/* UPI QR Code */}
                <div className="w-60 h-60 mx-auto rounded-2xl overflow-hidden border border-sky-100 shadow-sm mb-8">
                  <img
                    src="https://customer-assets.emergentagent.com/job_ice-prints/artifacts/lxok0x56_IMG_4502.PNG"
                    alt="UPI QR Code - Scan to Pay"
                    className="w-full h-full object-contain bg-white"
                  />
                </div>

                <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-400">
                  <span className="px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100">
                    Google Pay
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100">
                    PhonePe
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100">
                    Paytm
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100">
                    All UPI Apps
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ===== Footer Section ===== */
export const FooterSection = ({ siteContent }) => {
  const businessName = siteContent?.business_name || siteInfo.name;
  const location = siteContent?.location || siteInfo.location;
  const whatsappNumber = siteContent?.whatsapp_number || siteInfo.whatsappNumber;
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hi Frostyware 3D! I'm interested in your 3D printing services.")}`;
  const igBusiness = siteContent?.instagram_business || "";
  const igPersonal = siteContent?.instagram_personal || "";

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer id="contact" className="bg-slate-900 text-white relative overflow-hidden">
      {/* Top border accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-400/40 to-transparent" />
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-sky-900/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-xl bg-sky-500 flex items-center justify-center">
                <Snowflake className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold tracking-tight">
                {businessName}
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-xs mb-6">
              Premium 3D printing solutions in Chandigarh. Turning your
              imagination into reality, one layer at a time.
            </p>

            {/* Instagram follow chips */}
            {(igBusiness || igPersonal) && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Follow Us
                </div>
                {igBusiness && (
                  <a
                    href={igBusiness}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ig-business-link"
                    className="group flex items-center gap-2.5 px-3 py-2 rounded-xl bg-gradient-to-r from-pink-500/10 via-fuchsia-500/10 to-orange-500/10 border border-pink-500/20 hover:border-pink-400/50 transition-all duration-300 hover:translate-x-1"
                  >
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <Instagram className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-slate-500 leading-none">Business</div>
                      <div className="text-sm font-semibold text-white truncate group-hover:text-pink-200 transition-colors">@frostyware</div>
                    </div>
                    <ArrowUpRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-pink-300 transition-colors" />
                  </a>
                )}
                {igPersonal && (
                  <a
                    href={igPersonal}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ig-personal-link"
                    className="group flex items-center gap-2.5 px-3 py-2 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-slate-500 transition-all duration-300 hover:translate-x-1"
                  >
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <Instagram className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-slate-500 leading-none">Founder</div>
                      <div className="text-sm font-semibold text-white truncate group-hover:text-pink-200 transition-colors">@abhijeetmakan</div>
                    </div>
                    <ArrowUpRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-pink-300 transition-colors" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-5">
              Quick Links
            </h4>
            <div className="space-y-3">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="block text-sm text-slate-400 hover:text-sky-400 transition-colors duration-200"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-5">
              Contact Us
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-sky-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-400">
                  {location}
                </span>
              </div>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 group"
              >
                <WhatsAppIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-sm text-slate-400 group-hover:text-emerald-400 transition-colors duration-200">
                  Chat on WhatsApp
                </span>
                <ArrowUpRight className="w-3 h-3 text-slate-500 group-hover:text-emerald-400 transition-colors duration-200" />
              </a>
            </div>

            <Button
              onClick={() => window.open(whatsappLink, "_blank")}
              className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 text-sm shadow-md shadow-emerald-500/20 hover:shadow-lg transition-all duration-300"
            >
              <WhatsAppIcon className="w-4 h-4 mr-2" />
              Order on WhatsApp
            </Button>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-14 pt-8 border-t border-slate-800">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              &#169; {new Date().getFullYear()} {businessName}. All rights reserved.
              Made with precision in Chandigarh.
            </p>
            <a
              href="/privacy"
              data-testid="privacy-policy-link"
              className="text-sm text-slate-400 hover:text-sky-400 transition-colors duration-200"
            >
              Privacy Policy
            </a>
            <a
              href="/blog"
              data-testid="footer-blog-link"
              className="text-sm text-slate-400 hover:text-sky-400 transition-colors duration-200"
            >
              Blog
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ===== Floating WhatsApp Button ===== */
export const WhatsAppFloatingButton = ({ siteContent }) => {
  const [isVisible, setIsVisible] = useState(false);
  const whatsappNumber = siteContent?.whatsapp_number || siteInfo.whatsappNumber;
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hi Frostyware 3D! I'm interested in your 3D printing services.")}`;

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <a
      href={whatsappLink}
      target="_blank"
      rel="noopener noreferrer"
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-0 sm:gap-0 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-500 hover:scale-105 group ${
        isVisible
          ? "translate-y-0 opacity-100"
          : "translate-y-20 opacity-0"
      }`}
    >
      <div className="w-14 h-14 rounded-full flex items-center justify-center relative">
        <WhatsAppIcon className="w-7 h-7" />
        <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-15" />
      </div>
      <span className="pr-5 font-semibold text-sm hidden sm:block">
        Chat with us
      </span>
    </a>
  );
};
